import {
  StorageObjectVersion,
  StorageObject,
  StorageVersionActions,
} from "../../services/api/instances/storageApi/types/object_types";
import { StorageObjectValue } from "../../services/api/instances/storageApi/types/service_types";
import { useAppDispatch } from "../../services/redux/hooks/use-dispatch";

export const useStorageObjectVersions = <T extends StorageObjectValue>(
  versions: StorageObjectVersion<T>[],
  selectedVersion: StorageObjectVersion<T> | null,
  object: StorageObject<T> | null,
  actions: StorageVersionActions<T>,
) => {
  const dispatch = useAppDispatch();

  const createObjectVersion = (name: string) => {
    dispatch(actions.createVersion(name))
      .unwrap()
      .then((response: StorageObjectVersion<T>) => {
        const version = {
          ...response,
          value: response?.value,
        };
        dispatch(
          actions.setObjectVersions([
            ...versions,
            version,
          ] as StorageObjectVersion<T>[]),
        );
        dispatch(
          actions.setSelectedVersion(version as StorageObjectVersion<T>),
        );
        dispatch(
          actions.setSelectedObject({
            ...object,
            value: version?.value,
          } as StorageObject<T>),
        );
      });
  };

  const updateObjectVersion = (name: string) => {
    const updatedSelectedVersion = {
      ...selectedVersion,
      version_name: name,
    };
    const updatedObjectVersion = versions.map((version) => {
      if (version.version_id === updatedSelectedVersion.version_id) {
        return updatedSelectedVersion;
      }
      return version;
    });
    dispatch(
      actions.updateVersion(updatedSelectedVersion as StorageObjectVersion<T>),
    );
    dispatch(
      actions.setObjectVersions(
        updatedObjectVersion as StorageObjectVersion<T>[],
      ),
    );
    dispatch(
      actions.setSelectedVersion(
        updatedSelectedVersion as StorageObjectVersion<T>,
      ),
    );
  };

  const restoreObjectVersion = (id: string) => {
    const chosenVersion = versions.find((version) => version.version_id === id);
    const updatedSelectedObject = {
      ...object,
      value: chosenVersion?.value,
    };
    dispatch(
      actions.setSelectedVersion(chosenVersion as StorageObjectVersion<T>),
    );
    dispatch(actions.updateObject(updatedSelectedObject as StorageObject<T>));
  };

  const refreshVersion = () => {
    const chosenVersion = versions.find(
      (version) => version.version_id === selectedVersion?.version_id,
    );
    const updatedVersions = versions.filter(
      (version) => version.version_id !== chosenVersion?.version_id,
    );
    dispatch(actions.deleteVersion(chosenVersion?.version_id!));
    dispatch(actions.createVersion(chosenVersion?.version_name!))
      .unwrap()
      .then((response) => {
        const version = {
          ...response,
          value: object?.value,
        };
        dispatch(
          actions.setObjectVersions([
            ...updatedVersions,
            version,
          ] as StorageObjectVersion<T>[]),
        );
        dispatch(
          actions.setSelectedVersion(version as StorageObjectVersion<T>),
        );
        dispatch(
          actions.setSelectedObject({
            ...object,
            value: version.value,
          } as StorageObject<T>),
        );
      });
  };

  const deleteObjectVersion = () => {
    dispatch(actions.deleteVersion(selectedVersion?.version_id!));
  };

  return {
    versions,
    selectedVersion,
    createObjectVersion,
    updateObjectVersion,
    restoreObjectVersion,
    refreshVersion,
    deleteObjectVersion,
  };
};
